import { IconButton, IconProps, makeStyles, styled, Tab, Tabs, withStyles } from "@material-ui/core";
import CloseIcon from '@material-ui/icons/Close';
import { useLinked, useModel } from "@type-r/react";
import { InblockIcon } from "common/invoice-aging/iconography";
import { Button, CollapsableSection, Column, div, Flex, Loading } from "lqx-react";
import React, { useEffect } from 'react';
import { RemittanceAdvice } from "../api/remittanceAdvice";
import { HeaderTitle } from "../components/asset-details-pane-styles";
import { CopyRow } from "../components/copy-row";
import { ActivityLog } from '../invoices/invoice-activity-logs-tab';
import { handleDate } from "lqx-react/src/formatters";
import { RemittanceAdviceEditDTO } from "../api/remittanceAdviceDTO";
import { EditRemittanceAdviceTab } from "./remittance-advice-edit-tab";
import { Can } from "src/app/services/can";
import { UserEntitlementCatalogs } from "src/app/models/UserEntitlements";
import { RemittanceAdviceSendToTab } from "./remittance-advice-send-to-tab";

export const RemittanceAdviceDetailsPane = ({ remittanceNote, close, remittanceNoteId, remittanceAdviceData, $selectedTab }: { remittanceNote: RemittanceAdvice, close, remittanceNoteId, remittanceAdviceData, $selectedTab }) => {
    const classes = useRemittanceNoteDetailsPaneStyles();

    const inblockedIconClases = useInblockedIconStyles();

    const remittanceAdviceEdit = useModel(RemittanceAdvice);
    const remittanceAdviceEditDto = useModel(RemittanceAdviceEditDTO);

    const validationData = useLinked({});
    const isEdit = $selectedTab.value === 'edit';
    const assetUid = remittanceNote.uid;

    useEffect(() => {
        validationData.set({});
        remittanceAdviceEdit.set(remittanceNote.attributes);
    }, [$selectedTab.value]);

    const save = async () => {
        const upDate = () => {
            remittanceNote.assignFrom(remittanceAdviceEdit);
            $selectedTab.set('details')
        }
        const test = { ...remittanceAdviceData.attributes, dataset: remittanceAdviceEdit };
        await remittanceAdviceEditDto.update(remittanceNoteId, test, upDate, validationData);
    }

    const editOpen = async () => {
        $selectedTab.set("edit")
    }

    const cancelEdit = () => {
        $selectedTab.set("details")
    }

    return <Container>
        <Header>
            <IconButton aria-label="Close" size="small" className={classes.iconButton} onClick={() => close()}>
                <CloseIcon />
            </IconButton>
            <Flex alignCenter>
                {remittanceNote.isDigitized ? <>
                    <InblockIcon src="/assets/images/inblock_logo.png" classes={inblockedIconClases} />
                    <StatusWithoutMargin>{remittanceNote.state.name}</StatusWithoutMargin> </>
                    : <Status>{remittanceNote.state.name}</Status>}
            </Flex>
            <HeaderTitle><>Remittance Note {remittanceNote.number ? remittanceNote.number : ''}</></HeaderTitle>
        </Header>
        {Can(UserEntitlementCatalogs.EditVaultRemittanceAdvicesPage) && (
            <ButtonsBlock>
                <Flex row justifyCenter>
                    {!isEdit && <EditButton onClick={editOpen} color="primary">Edit</EditButton>}
                    {isEdit && <EditButton onClick={save} color="primary">Save</EditButton>}
                    {isEdit && <EditButton onClick={cancelEdit} color="primary">Cancel</EditButton>}
                </Flex>
            </ButtonsBlock>
        )}
        <RemittanceNoteTabs value={$selectedTab.value}
            variant="scrollable"
            scrollButtons="auto"
            onChange={(evt, newValue) => $selectedTab.set(newValue)} >
            <RemittanceNoteTab value="details" label=" Details" />
            <RemittanceNoteTab value="activityLog" label="Activity Log" />
            {remittanceNote.isDigitized && <RemittanceNoteTab value="sendTo" label=" Send To" disabled={isEdit} />}
        </RemittanceNoteTabs>
        <RemittanceNoteTabContent>
            {$selectedTab.value === 'details' && <Details remittanceNote={remittanceNote} remittanceNoteId={remittanceNoteId} />}
            {$selectedTab.value === 'edit' && <EditRemittanceAdviceTab remittanceAdvice={remittanceAdviceEdit} errors={validationData.value} />}
            {$selectedTab.value === 'activityLog' && <ActivityLog assetId={`${remittanceNoteId}`} />}
            {$selectedTab.value === 'sendTo' && <RemittanceAdviceSendToTab assetId={remittanceNoteId} assetUid={assetUid} />}
        </RemittanceNoteTabContent>
    </Container>

};

const Details = ({ remittanceNote, remittanceNoteId }: { remittanceNote: RemittanceAdvice, remittanceNoteId }) => {
    const classes = useRemittanceNoteDetailsPaneStyles();
    const sectionStyles = SectionStyles();
    const applyPercentageFormat = (num: number) => (num ? num : 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + '%';

    return (<>
        <CollapsableSection title="General" titleClassName={sectionStyles.sectionTitle} noBorder noShadow>
            <Column>
                <CopyRow label="Data Owner" value={remittanceNote.dataHolder.name} />

                <CopyRow label="Payor" value={remittanceNote.payor.company.name} />

                <CollapsableSection title="BANK ACCOUNT INFORMATION" titleClassName={sectionStyles.sectionTitle} noBorder noShadow notExpanded>
                    <Column>
                        <CopyRow label="Name" value={remittanceNote.payor.bankName} />
                        <CopyRow label="Alias" value={remittanceNote.payor.bankAlias} />
                        <CopyRow label="Account Number" value={remittanceNote.payor.bankAccountNumber} />
                        <CopyRow label="Branch Code" value={remittanceNote.payor.bankBranchCode} />
                    </Column>
                </CollapsableSection>

                <CopyRow label="Payee" value={remittanceNote.payee.company.name} />

                <CollapsableSection title="BANK ACCOUNT INFORMATION" titleClassName={sectionStyles.sectionTitle} noBorder noShadow notExpanded>
                    <Column>
                        <CopyRow label="Name" value={remittanceNote.payee.bankName} />
                        <CopyRow label="Alias" value={remittanceNote.payee.bankAlias} />
                        <CopyRow label="Account Number" value={remittanceNote.payee.bankAccountNumber} />
                        <CopyRow label="Branch Code" value={remittanceNote.payee.bankBranchCode} />
                    </Column>
                </CollapsableSection>

                <CopyRow label="Payment Date" date={!!remittanceNote.paymentDate} value={handleDate(remittanceNote.paymentDate)} />
                <CopyRow label="Payment Currency" value={remittanceNote.paymentCurrency} />
                <CopyRow label="Payment Amount" currency value={remittanceNote.paymentAmount} />
                <CopyRow label="Payment Reference Number" value={remittanceNote.paymentReferenceNumber} />
                <CopyRow label="Relationship Id" value={remittanceNote.relationShipId} />
                <CopyRow label="Asset ID" value={remittanceNoteId} />
                <CopyRow label="Asset UID" value={remittanceNote.uid} />
                <CopyRow label="Import Job ID" value={remittanceNote.importJobId} hyperlinkHref={`/import-jobs/?importJobId=${remittanceNote.importJobId}`} />
                <CopyRow label="Created Date" date={!!remittanceNote.createdOn} value={handleDate(remittanceNote.createdOn)} />
                <CopyRow label="Updated Date" date={!!remittanceNote.updatedOn} value={handleDate(remittanceNote.updatedOn)} />

                <CollapsableSection title="INVOICE INFORMATION" titleClassName={sectionStyles.sectionTitle} noBorder noShadow notExpanded>
                    <Column>
                        <CopyRow label="Invoice Number" value={remittanceNote.invoice.number} />
                        <CopyRow label="Supplier Name" value={remittanceNote.invoice.supplier.name} />
                        <CopyRow label="Buyer Name" value={remittanceNote.invoice.buyer.name} />
                        <CopyRow label="Invoice Date" date={!!remittanceNote.invoice.invoiceDate} value={handleDate(remittanceNote.invoice.invoiceDate)} />
                        <CopyRow label="Invoice Due Date" date={!!remittanceNote.invoice.invoiceDueDate} value={handleDate(remittanceNote.invoice.invoiceDueDate)} />
                        <CopyRow label="Face Value" currency value={remittanceNote.invoice.faceValue} />
                        <CopyRow label="Currency" value={remittanceNote.invoice.currency} />
                        <CopyRow label="Discount" value={remittanceNote.invoice.discount} />
                        <CopyRow label="Net Amount" currency value={remittanceNote.invoice.netAmount} />
                        <CopyRow label="Applied Amount" currency value={remittanceNote.invoice.appliedAmount} />
                        <CopyRow label="Write Off" currency value={remittanceNote.invoice.writeOff} />
                        <CopyRow label="Details" value={remittanceNote.invoice.details} />
                    </Column>
                </CollapsableSection>
            </Column>
        </CollapsableSection>
    </>);
}

const Container = div({
    backgroundColor: 'white',
    height: '100%',
    padding: '0px',
    marginTop: '26px',
});

const Header = div({
    display: 'contents',
    width: '100%',
    height: '100%',
    border: '1px solid #E2F0F7',
    paddingTop: 0
});

const Status = div({
    color: '#63a9dc',
    height: '15px',
    fontSize: '12px',
    marginLeft: '21px',
    fontWeight: 500,
    lineHeight: '15px',
});

const StatusWithoutMargin = div({
    color: '#63a9dc',
    height: '15px',
    fontSize: '12px',
    fontWeight: 500,
    lineHeight: '15px',
});

const RemittanceNoteTabs = withStyles({
    indicator: {
        display: 'none'
    },
    root: {
        marginTop: '8px'
    }
})((props: any) => <Tabs {...props} TabIndicatorProps={{ children: <span /> }} />);

const RemittanceNoteTab = withStyles({
    root: {
        minWidth: '73px',
        display: 'flex',
        flexDirection: 'row',
        alignItems: 'flex-start',
        padding: '3px 10px 6px',
        position: 'static',
        height: '26px',
        left: '46.58px',
        background: (props: any) => props.selected ? '#DCEEFD' : 'white',
        fontWeight: (props: any) => props.selected ? 600 : 400,
        borderRadius: '4px',
        flex: 'none',
        margin: '0px 20px',
        textTransform: 'none'
    },
})((props: any) => <Tab disableRipple {...props} />);

const RemittanceNoteTabContent = div({
    border: '1px solid #E2F0F7',
    marginTop: '10px',
    fontSize: '13px',
    fontWeight: 500,
    lineHeight: '15px',
});

const useRemittanceNoteDetailsPaneStyles = makeStyles({
    iconButton: {
        display: 'block',
        marginLeft: 'auto',
        marginTop: '10px',
        marginRight: '10px',
    },
    textAlignRight: {
        textAlign: 'right',
        paddingRight: '20px',
    }
})

const LiquidxIcon = ({ ...rest }: Partial<IconProps>) => {
    const classes = useLiquidxIconStyles(rest);
    return <img className={classes.root} src="/assets/images/liquidx.png" />
}

const useLiquidxIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '140px',
        marginTop: '30px'
    }
});

const useInblockedIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '15px',
        marginRight: '2px',
        marginLeft: '21px',
    }
});

const useInblockIconStyles = makeStyles({
    root: {
        display: 'flex',
        width: '140px',
        marginTop: '30px'
    }
});

const SendToButton = styled(Button)({
    minWidth: '0px',
    marginTop: '20px',
    fontSize: '12px'
});

const SendToContent = div({
    padding: '30px'
});

const SectionStyles = makeStyles({
    sectionTitle: {
        color: '#63a9dc',
        fontSize: '13px',
        marginRight: '1em',
        display: 'flex',
        alignItems: 'center'
    },
})

const ConfirmButton = styled(Button)({
    marginBottom: '10px',
    backgroundColor: '#4197d6',
    border: '0px',
    color: 'white',
    borderRadius: '10px',
    height: '25px',
    marginLeft: 'auto',
    marginTop: '20px',
    '&:hover': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        marginLeft: 'auto',
    },
    '&:active': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        marginLeft: 'auto',
    },
    '&:focus': {
        marginBottom: '10px',
        backgroundColor: '#4197d6',
        border: '0px',
        color: 'white',
        borderRadius: '10px',
        height: '25px',
        marginLeft: 'auto',
    }
});

const EditButton = styled(Button)({
    marginTop: '10px',
    height: '23px',
    borderRadius: '50px',
    padding: '5px, 10px, 5px, 10px',
    fontFamily: 'Montserrat',
    fontSize: '11px',
    fontStyle: 'normal',
    fontWeight: 400,
    lineHeight: '13px',
    letterSpacing: '0px',
    textAlign: 'center'
});

export const ButtonsBlock = div({
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'flex-end',
    alignItems: 'center',
    paddingRight: '6px'
});